/**
 * TicketTypes — the ticket-type catalog of whichever event is currently
 * open in `event-form`, its list view-model, the `showAfterSoldOut`
 * chain-integrity checks (refuse cycles/self-reference, warn hard
 * on a descending chained price, warn when a chained predecessor carries a
 * `salesWindow.closesAt`), the day-derivation reads `day-clone-sheet.view.ts`
 * groups its source-day picker from (a type's own day lives only in its
 * `entryWindow`, never a stored field), and the write-intent channel every
 * ticket-type-CRUD view dispatches through.
 *
 * `ticketTypesEventId$` is the side-channel `event-form` pushes its open
 * event's id into; ticket-types.sync.ts (web-side) is the sole subscriber
 * of that channel (re-scoping the Firestore listener), of
 * `ticketTypesAction$` (performing the write, reconciled back via
 * `ticketTypesResult$`), and of `quotaRequest$` (reading one type's own
 * drawn capacity, answered on `quotaResult$`) — all three keyed by
 * requestId.
 */
import { computed, state } from "@mhmo91/schmancy/state";
import { type LocalizedText, resolveLocalizedText } from "@momo/i18n";
import {
	type Dependents,
	type Event,
	type Location,
	type TicketTemplateVariant,
	TicketType,
} from "@momo/types";
import { BehaviorSubject, Subject } from "rxjs";
import { uiLanguage } from "../../i18n/localize.ts";

export type TicketTypeWithId = TicketType.TicketType & { id: string };

// ── Live state singletons ─────────────────────────────────────────────────────

/** Which event's ticketTypes subcollection is currently listened to — null closes the listener. */
export const ticketTypesEventId$ = new BehaviorSubject<string | null>(null);

export const ticketTypesState = state<readonly TicketTypeWithId[]>(
	"events/ticketTypes",
).memory([]);

export const ticketTypesStatus = state<"loading" | "ready" | "error">(
	"events/ticketTypesStatus",
).memory("loading");

// ── Chain integrity — `showAfterSoldOut` cycles, self-reference, price, window ──

/** True when pointing `id` (null for a not-yet-created type) behind `candidate` would self-reference or complete a cycle. */
export function chainCreatesCycle(
	id: string | null,
	candidate: string,
	types: readonly TicketTypeWithId[],
): boolean {
	if (!candidate) return false;
	if (id !== null && candidate === id) return true;
	const seen = new Set<string>();
	let cursor: string | undefined = candidate;
	while (cursor) {
		if (id !== null && cursor === id) return true;
		if (seen.has(cursor)) return true; // a pre-existing cycle in the data — refuse rather than loop forever
		seen.add(cursor);
		cursor = types.find((t) => t.id === cursor)?.showAfterSoldOut ?? undefined;
	}
	return false;
}

/** The live predecessor a `showAfterSoldOut` id points at, or undefined when unset or not found. */
export function chainPredecessor(
	showAfterSoldOut: string,
): TicketTypeWithId | undefined {
	return ticketTypesState.value.find((t) => t.id === showAfterSoldOut);
}

/** Hard-warn case: the successor's price is lower than the predecessor it chains behind — takes
 *  the predecessor's raw price so a draft predecessor (no salesWindow, no id yet) satisfies it too. */
export function chainPriceDescends(
	ownPrice: number,
	predecessorPrice: number,
): boolean {
	return ownPrice < predecessorPrice;
}

/** Warn case: a chained predecessor that carries an EXPLICIT `salesWindow`
 *  override reveals the successor on that window's own close, short of
 *  sellout — guards the raw field, never the resolved window (every resolved
 *  window has a closesAt by construction, so that would fire on nearly every
 *  chain and destroy the signal). */
export function chainPredecessorHasClosesAt(
	predecessor: TicketTypeWithId,
): boolean {
	return predecessor.salesWindow?.closesAt != null;
}

/** Every type reachable from `seeds` along `showAfterSoldOut`, in both
 *  directions and transitively, seeds included. A scope naming one link of a
 *  chain silently stops applying the moment that link sells out and its
 *  successor takes its place, so the voucher form offers the closure rather
 *  than leaving an operator to discover the gap from a code that quietly
 *  stopped working. Cycle-safe by the visited set — a pre-existing cycle in
 *  the data terminates rather than looping. */
export function chainClosure(
	seeds: readonly string[],
	types: readonly TicketTypeWithId[],
): readonly string[] {
	const reached = new Set(seeds);
	const queue = [...seeds];
	for (let cursor = queue.shift(); cursor !== undefined; cursor = queue.shift())
		for (const id of [
			types.find((t) => t.id === cursor)?.showAfterSoldOut ?? null,
			...types.filter((t) => t.showAfterSoldOut === cursor).map((t) => t.id),
		])
			if (id !== null && !reached.has(id)) {
				reached.add(id);
				queue.push(id);
			}
	return [...reached];
}

// ── Day derivation — `entryWindow` is the only source of a type's own day ────

/** The calendar day an instant falls on, in a given zone, as `YYYY-MM-DD` —
 *  TicketType.dayKeyOf (@momo/types), the one day key the shop's chips, this
 *  form and the console all read, so no two of them can name a day
 *  differently. */
export function dayOf(instant: Date, timezone: string): string {
	return TicketType.dayKeyOf(instant.getTime(), timezone);
}

/** Every calendar day a type's entry window overlaps, in the given zone — one
 *  day for an ordinary day ticket, several for a multi-day pass. Empty for a
 *  type with no entryWindow: resolveEntryWindow's own inherit rule admits it
 *  for the whole event, so it is tied to no single day. */
export function daysCovered(
	type: Pick<TicketType.TicketType, "entryWindow">,
	timezone: string,
): readonly string[] {
	if (!type.entryWindow) return [];
	return TicketType.daysCovered(
		{
			opensAt: type.entryWindow.opensAt.toMillis(),
			closesAt: type.entryWindow.closesAt.toMillis(),
		},
		timezone,
	);
}

/** The one day a type's entry window is entirely confined to — null for a
 *  type with no entryWindow (whole event) or one spanning more than one day.
 *  Only a single-day type has one clean day to copy from: a whole-event or
 *  multi-day type already applies wherever it's asked to and needs no
 *  cloning, per the entity's own "day is derived, not stored" rule. */
export function startDayOf(
	type: Pick<TicketType.TicketType, "entryWindow">,
	timezone: string,
): string | null {
	const days = daysCovered(type, timezone);
	return days.length === 1 ? (days[0] ?? null) : null;
}

/** Every calendar day this event's own single-day types occupy, each paired
 *  with that day's own types — the day-clone sheet's source-day candidate
 *  set, so the sheet reads it rather than assembling the grouping itself. */
export function daySourceGroups(
	timezone: string,
): ReadonlyMap<string, readonly TicketTypeWithId[]> {
	const groups = new Map<string, TicketTypeWithId[]>();
	for (const t of ticketTypesState.value) {
		const day = startDayOf(t, timezone);
		if (day === null) continue;
		const list = groups.get(day);
		if (list) list.push(t);
		else groups.set(day, [t]);
	}
	return groups;
}

// ── Page view-model ────────────────────────────────────────────────────────

export const ticketTypesPageView = computed(() => {
	const types = ticketTypesState.value;
	const locale = uiLanguage();
	const rows = types
		.map((t) => {
			const predecessor =
				types.find((p) => p.id === t.showAfterSoldOut) ?? null;
			return {
				id: t.id,
				kind: t.kind,
				name: resolveLocalizedText(t.nameByLocale, t.name, locale),
				price: t.price,
				capacity: t.capacity,
				order: t.order,
				predecessorName:
					predecessor === null
						? null
						: resolveLocalizedText(
								predecessor.nameByLocale,
								predecessor.name,
								locale,
							),
				priceDescends:
					predecessor !== null &&
					chainPriceDescends(t.price, predecessor.price),
				predecessorHasClosesAt:
					predecessor !== null && chainPredecessorHasClosesAt(predecessor),
			};
		})
		// The operator's own chosen position, tie-broken on the label the
		// operator actually reads — never on the source string behind it, since
		// a list ordered by a language nobody on this screen is reading looks
		// unsorted.
		.toSorted((a, b) => a.order - b.order || a.name.localeCompare(b.name));
	return { rows, status: ticketTypesStatus.value };
});

/** Candidate `showAfterSoldOut` predecessors for editing `id` (null when creating) — excludes self and anything that would cycle. */
export function chainOptions(id: string | null): readonly TicketTypeWithId[] {
	return ticketTypesState.value.filter(
		(t) => !chainCreatesCycle(id, t.id, ticketTypesState.value),
	);
}

// ── write-intent channel ────────────────────────────────────────────────────

export type TicketTypeWrite = {
	kind: TicketType.TicketType["kind"];
	name: string;
	/** The type's label per UI language, as the operator accepted it — what
	 *  delivery freezes onto each ticket in the buyer's own language. Undefined
	 *  leaves whatever is stored untouched; an explicit null clears the map,
	 *  which is how an edit to `name` retires copy the operator no longer
	 *  stands behind.
	 *
	 *  Explicitly `| undefined` under `exactOptionalPropertyTypes`: the form
	 *  computes "leave it alone" as a value on every save rather than choosing
	 *  between two object literals, so undefined is passed, not omitted. */
	nameByLocale?: LocalizedText | null | undefined;
	/** Buyer-facing copy the storefront offers behind the type's own name and
	 *  the `info` glyph beside it — null is the ordinary state, and the state
	 *  every surface holds its layout against. */
	description: string | null;
	/** The buyer-facing copy per UI language, bounded at 200 characters in
	 *  every one of them. Cleared the same way `nameByLocale` is. */
	descriptionByLocale?: LocalizedText | null | undefined;
	vatRate: number;
	price: number;
	capacity: number;
	/** The operator's chosen display position among this event's ticket types
	 *  — a create leaves it at the current end of the order, an edit carries
	 *  the type's own stored position forward untouched. */
	order: number;
	showAfterSoldOut: string | null;
	/** The operator's own audience answer, or null for unanswered — null is
	 *  not "buyer": TicketType.resolveClaimableBy reads an unanswered free type
	 *  as staff-only, and keeping the absence writable is what lets a type
	 *  re-priced away from €0 pick the shop back up without a second visit. */
	claimableBy: NonNullable<TicketType.TicketType["claimableBy"]> | null;
	/** Whether a person admits this type at the door rather than a decode —
	 *  false is a real answer (the ordinary scan-and-go door), never an
	 *  absence, which is why it is a boolean here and not a nullable one. */
	verifyAtDoor: boolean;
	/** The one thing staff is asked to check before admitting, or null — the
	 *  whole instruction the door's hold card carries, so "" would leave that
	 *  card asking for a confirmation of nothing. */
	doorNote: string | null;
	volumeDiscounts: readonly { minQty: number; unitPrice: number }[];
	salesWindow: { opensAt: Date; closesAt: Date } | null;
	/** When this type ADMITS at the door, as against salesWindow above, which
	 *  says when it may be BOUGHT — null is the inherit state itself, resolved
	 *  at read time by TicketType.resolveEntryWindow against the event's own
	 *  schedule, and written to Firestore as absence so a later
	 *  event.schedule edit keeps propagating. */
	entryWindow: { opensAt: Date; closesAt: Date } | null;
	locationId: string | null;
	/** Rendering-variant override for this type alone — null inherits the
	 *  event's own templateVariant. */
	templateVariant: TicketTemplateVariant | null;
};

export type TicketTypesAction =
	| {
			kind: "create";
			requestId: string;
			eventId: string;
			body: TicketTypeWrite;
	  }
	| {
			kind: "update";
			requestId: string;
			eventId: string;
			id: string;
			body: TicketTypeWrite;
	  }
	| { kind: "delete"; requestId: string; eventId: string; id: string }
	| {
			kind: "reorder";
			requestId: string;
			eventId: string;
			/** every persisted type's id, in the operator's chosen order — index becomes `order` */
			orderedIds: readonly string[];
	  }
	| {
			kind: "cloneDay";
			requestId: string;
			eventId: string;
			/** The source day's own rows, captured once by the sheet at pick time —
			 *  the single-request channel carries them through unchanged rather than
			 *  the write reconciler re-reading ticketTypesState at commit time, so a
			 *  concurrent edit to the source day between pick and submit is reflected
			 *  in neither direction (Flagged assumption #8). */
			sourceTypes: readonly TicketTypeWithId[];
			/** `YYYY-MM-DD[]`, excludes the source day. */
			targetDays: readonly string[];
			/** TicketType.resolveEffectiveTimezone's own second argument, read
			 *  fresh by the sheet at submit time (mirrors TicketTypeForm.save()'s
			 *  own "read fresh at save time" venue resolution) — each source
			 *  type's own `locationId` override resolves under ITS venue's zone,
			 *  never one event-wide zone every clone shares. */
			event: Pick<Event.Event, "locationId">;
			/** TicketType.resolveEffectiveTimezone's own third argument — the
			 *  keyed venue catalog. */
			locations: Record<string, Pick<Location.Location, "timezone">>;
	  };

export const ticketTypesAction$ = new Subject<TicketTypesAction>();

export interface TicketTypesResult {
	readonly requestId: string;
	readonly kind: TicketTypesAction["kind"];
	readonly ok: boolean;
	readonly id?: string;
	readonly error?: string;
	/** The census the delete door read, present only on a REFUSED delete —
	 * what still names the document, in counts. Its presence is the
	 * discriminant: a refusal is a normal answer with real numbers, not a
	 * failure, so `ok` is false and `error` is absent, and the form opens
	 * <delete-refusal> over this rather than a generic toast. */
	readonly dependents?: Dependents.Dependents;
	/** Present only on a "cloneDay" result — each target day's own outcome, so
	 *  a partial commit (one day's batch rejected while a sibling day's own
	 *  batch already landed) is visible rather than merged into one boolean —
	 *  writeBatch.commit() is atomic only within its own per-day call, never
	 *  across days (Reasoning). `ok` is true only once every day committed. */
	readonly perDay?: Record<string, { created: number; commitOk: boolean }>;
}

export const ticketTypesResult$ = new Subject<TicketTypesResult>();

// ── read-intent channel — one ticket type's own drawn capacity ──────────────

/** Ask for one ticket type's `quota/inventory` consumed count. Answered on
 *  `quotaResult$`, keyed by requestId. ticket-types.sync.ts (web-side) is the
 *  sole subscriber. */
export interface QuotaRequest {
	readonly requestId: string;
	readonly eventId: string;
	readonly typeId: string;
}

export const quotaRequest$ = new Subject<QuotaRequest>();

export interface QuotaResult {
	readonly requestId: string;
	/** Seats already drawn from this type's capacity, or null when the figure
	 *  could not be read — a denied read (the doc is gated on
	 *  `tickets.reports.view` + mfa), a quota doc that does not exist yet, or a
	 *  failed one. Null is not zero: a caller that states a count it could not
	 *  read would be stating a fabrication. */
	readonly consumed: number | null;
}

export const quotaResult$ = new Subject<QuotaResult>();
